import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import swal from 'sweetalert';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useReactToPrint } from 'react-to-print';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../Filters/Common/filtering.css';
import ERROR from '../../../components/Validation/ErrorMessage';
import COMMON_API from '../../../Api/CommonApi';
import DEBIT_NOTE_API from '../../../Api/DebitNoteApi';
import COMMONVARIABLES from '../../Utilities/constants';
import Formatting from '../../Utilities/format';
import { useDispatch } from 'react-redux';
import { getLoggedInUser, getRefreshToken } from '../../../../services/AuthService';
import { Logout, navtoggle } from '../../../../store/actions/AuthActions';
import Logo from '../../../../pictures/bw1.png';

const selectTheme = (theme) => ({
	...theme,
	borderRadius: '6px',
	colors: {
		...theme.colors,
		primary25: '#b3b3b3',
		primary: 'black',
		neutral80: 'data-theme-version' === 'dark' ? 'white' : '',
	},
});

export const DebitNoteAdd = () => {
	const paramData = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const BASE_URL = process.env.REACT_APP_BASE_URL;
	const buttonRef = useRef(null);
	const SUPPLIERTYPE = COMMONVARIABLES.SUPPLIERTYPE;

	const [title, setTitle] = useState('Add Debit Note');
	const [btnTitle, setBtnTitle] = useState('Save');
	const [transNo, setTransNo] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [buttonFlag, setButtonFlag] = useState(false);

	const [debitNoteNo, setDebitNoteNo] = useState('');
	const [debitNoteDate, setDebitNoteDate] = useState(new Date().toISOString().slice(0, 10));
	const [narration, setNarration] = useState('');

	const [supplierList, setSupplierList] = useState([]);
	const [supplierNameOptions, setSupplierNameOptions] = useState([]);
	const [supplierType, setSupplierType] = useState({ label: 'Registered', value: 1 });
	const [supplierName, setSupplierName] = useState({});
	const [supplierAddress, setSupplierAddress] = useState('');
	const [supplierCity, setSupplierCity] = useState('');
	const [supplierContact, setSupplierContact] = useState('');
	const [supplierGstin, setSupplierGstin] = useState('');

	const [itemDetails, setItemDetails] = useState([]);
	const [isEdit, setIsEdit] = useState(0);
	const [currentSno, setCurrentSno] = useState(0);

	const [currentItemName, setCurrentItemName] = useState('');
	const [currentQty, setCurrentQty] = useState('');
	const [currentRate, setCurrentRate] = useState('');
	const [currentAmount, setCurrentAmount] = useState(0);
	const [gstPercent, setGstPercent] = useState(5);
	const [loadedTotals, setLoadedTotals] = useState(null);

	// Letters, numbers, space, dot, hyphen, slash, ampersand, comma
	const ITEM_NAME_PATTERN = /^[A-Za-z0-9.\-/&, ]*$/;
	const sanitizeItemName = (value) =>
		String(value || '')
			.replace(/[^A-Za-z0-9.\-/&, ]/g, '')
			.replace(/\s+/g, ' ');

	const contentToPrint = useRef(null);
	const [isPrint, setIsPrint] = useState(0);
	const [localUserDetails, setLocalUserDetails] = useState(null);
	const [printData, setPrintData] = useState({
		debit_note_no: '',
		debit_note_date: '',
		supplier_name: '',
		supplier_address: '',
		supplier_city: '',
		supplier_gstin: '',
		supplier_contact: '',
		narration: '',
		subtotal: 0,
		gst_amount: 0,
		round_off: 0,
		total_amount: 0,
		total_qty: 0,
		item_details: [],
	});

	useEffect(() => {
		dispatch(navtoggle()); // close side menu like Purchase Add
		const lcluserDetails = localStorage.getItem('userDetails');
		if (lcluserDetails) {
			setLocalUserDetails(JSON.parse(lcluserDetails));
		}
		fetchMasterData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isPrint === 1) {
			handlePrint(null, () => contentToPrint.current);
			setIsPrint(0);
		}
	}, [printData, isPrint]); // eslint-disable-line react-hooks/exhaustive-deps

	const handlePrint = useReactToPrint({
		documentTitle: 'Debit Note',
		onAfterPrint: () => {
			navigate('/debit-note-list');
		},
		removeAfterPrint: true,
	});

	useEffect(() => {
		const editId = Number(paramData?.state?.debit_note_id || paramData?.state?.transNo || 0);
		if (editId > 0 && supplierList.length > 0 && Number(transNo) !== editId) {
			setTransNo(editId);
			setTitle('Update Debit Note');
			setBtnTitle('Update');
			editLoadData(editId);
		}
	}, [supplierList, paramData?.state]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!supplierName?.value) {
			setSupplierAddress('');
			setSupplierCity('');
			setSupplierContact('');
			setSupplierGstin('');
			return;
		}
		const supplier = supplierList.find((e) => e?.sm_supplier_code === supplierName?.value);
		if (supplier) {
			setSupplierAddress(supplier?.sm_address || '');
			setSupplierCity(supplier?.sm_city || '');
			setSupplierContact(supplier?.sm_mobile_number || '');
			setSupplierGstin(supplier?.sm_gstin || '');
		}
	}, [supplierName, supplierList]);

	useEffect(() => {
		const qty = Number(currentQty) || 0;
		const rate = Number(currentRate) || 0;
		setCurrentAmount(Formatting('two_decimal', qty * rate));
	}, [currentQty, currentRate]);

	const subtotal = useMemo(() => {
		if (itemDetails.length > 0) {
			return Formatting(
				'two_decimal',
				itemDetails.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
			);
		}
		if (loadedTotals?.subtotal != null) {
			return Formatting('two_decimal', loadedTotals.subtotal);
		}
		return Formatting('two_decimal', 0);
	}, [itemDetails, loadedTotals]);

	const gstAmount = useMemo(() => {
		if (itemDetails.length > 0) {
			return Formatting(
				'two_decimal',
				Number(subtotal) * (Number(gstPercent) / 100)
			);
		}
		if (loadedTotals?.gst_amount != null) {
			return Formatting('two_decimal', loadedTotals.gst_amount);
		}
		return Formatting('two_decimal', Number(subtotal) * (Number(gstPercent) / 100));
	}, [itemDetails, loadedTotals, subtotal, gstPercent]);

	// Before round-off (2 decimals)
	const amountBeforeRoundOff = useMemo(() => {
		if (itemDetails.length > 0) {
			return Formatting('two_decimal', Number(subtotal) + Number(gstAmount));
		}
		if (loadedTotals?.subtotal != null && loadedTotals?.gst_amount != null) {
			return Formatting(
				'two_decimal',
				Number(loadedTotals.subtotal) + Number(loadedTotals.gst_amount)
			);
		}
		return Formatting('two_decimal', Number(subtotal) + Number(gstAmount));
	}, [itemDetails, loadedTotals, subtotal, gstAmount]);

	const roundOff = useMemo(() => {
		if (itemDetails.length > 0) {
			const rounded = Formatting('round_off', amountBeforeRoundOff);
			return Formatting('two_decimal', Number(rounded) - Number(amountBeforeRoundOff));
		}
		if (loadedTotals?.round_off != null) {
			return Formatting('two_decimal', loadedTotals.round_off);
		}
		const rounded = Formatting('round_off', amountBeforeRoundOff);
		return Formatting('two_decimal', Number(rounded) - Number(amountBeforeRoundOff));
	}, [itemDetails, loadedTotals, amountBeforeRoundOff]);

	const grandTotal = useMemo(() => {
		if (itemDetails.length > 0) {
			return Formatting('round_off', amountBeforeRoundOff);
		}
		if (loadedTotals?.total_amount != null) {
			return Formatting('round_off', loadedTotals.total_amount);
		}
		return Formatting('round_off', amountBeforeRoundOff);
	}, [itemDetails, loadedTotals, amountBeforeRoundOff]);

	const totalQty = useMemo(() => {
		if (itemDetails.length > 0) {
			return itemDetails.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
		}
		if (loadedTotals?.total_qty != null) {
			return Number(loadedTotals.total_qty);
		}
		return 0;
	}, [itemDetails, loadedTotals]);

	async function handleUnauthorized(retryFn) {
		const refresh = await getRefreshToken();
		if (refresh) {
			if (Number(refresh) === COMMONVARIABLES.STATUS.EXPIRED_TOKEN) {
				Logout();
			} else {
				await retryFn();
			}
		}
	}

	const onChangeSupplierType = (event) => {
		setSupplierType(event);
		const supplier_array = supplierList.filter((e) => e.supplier_type === event?.value);
		setSupplierNameOptions(supplier_array || []);
		setSupplierName({});
	};

	async function fetchMasterData() {
		try {
			setIsLoading(true);
			const user = getLoggedInUser();
			const localFy = localStorage.getItem('fy');
			const listParams = { status_code: 1, user_id: user?.id };
			const debitNoParams = { fy: localFy ? Number(localFy) : 0, user_id: user?.id };
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };

			const [supplierData, debitNoData] = await Promise.all([
				axios.post(BASE_URL + COMMON_API.SUPPLIER_MASTER_LIST, listParams, config),
				axios.post(BASE_URL + DEBIT_NOTE_API.DEBIT_NOTE_NO, debitNoParams, config).catch(() => null),
			]);

			if (
				supplierData?.data?.body?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED ||
				debitNoData?.data?.body?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED
			) {
				await handleUnauthorized(fetchMasterData);
				setIsLoading(false);
				return;
			}

			if (supplierData?.data?.body?.status === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS) {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}

			if (supplierData?.data?.body?.supplierList) {
				const allSuppliers = supplierData.data.body.supplierList;
				setSupplierList(allSuppliers);
				const supplier_array = allSuppliers.filter((e) => e.supplier_type === supplierType?.value);
				setSupplierNameOptions(supplier_array || []);
			}
			// Max No API: { status: 200, body: { debit_note_number, debit_note_date } }
			// Use number only; keep Debit Date as current local date (API ISO date shifts to previous day in IST)
			const maxBody = debitNoData?.data?.body || {};
			const maxStatus = debitNoData?.data?.status ?? maxBody.status;
			if (
				maxStatus === COMMONVARIABLES.STATUS.SUCCESS ||
				maxBody.debit_note_number != null ||
				maxBody.debit_note_no != null
			) {
				const maxNo = maxBody.debit_note_number ?? maxBody.debit_note_no ?? maxBody.max_no ?? '';
				if (maxNo !== '' && maxNo != null) {
					setDebitNoteNo(String(maxNo));
				}
			}
			if (Number(transNo) === 0) {
				const today = new Date();
				const y = today.getFullYear();
				const m = String(today.getMonth() + 1).padStart(2, '0');
				const d = String(today.getDate()).padStart(2, '0');
				setDebitNoteDate(`${y}-${m}-${d}`);
			}
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			console.error('Error fetching debit note master data:', error);
			if (error.code === COMMONVARIABLES.STATUS.NETWORK_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.NETWORK_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
			if (error.code === COMMONVARIABLES.STATUS.BAD_REQUEST_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.BAD_REQUEST_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		}
	}

	async function editLoadData(transactionNo) {
		try {
			setIsLoading(true);
			const user = getLoggedInUser();
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
			const editLoadDataRes = await axios.post(
				BASE_URL + DEBIT_NOTE_API.EDIT_DEBIT_NOTE,
				{
					user_id: Number(user?.id),
					debit_note_id: Number(transactionNo),
				},
				config
			);

			const resStatus = editLoadDataRes?.data?.body?.status ?? editLoadDataRes?.data?.status;
			if (resStatus === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
				setIsLoading(false);
				await handleUnauthorized(() => editLoadData(transactionNo));
				return;
			}

			if (resStatus === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS) {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
				setIsLoading(false);
				return;
			}

			const body = editLoadDataRes?.data?.body || {};
			// Edit load: debitNoteList[0] = header, debitNoteItemList = items
			const headerList = body.debitNoteList || [];
			const header = Array.isArray(headerList) && headerList.length > 0 ? headerList[0] : null;
			const details = Array.isArray(body.debitNoteItemList) ? body.debitNoteItemList : [];

			if (header) {
				setTransNo(Number(header.dn_debit_note_id || transactionNo));

				const noteNo = header.dn_debit_note_number;
				if (noteNo != null && noteNo !== '') {
					setDebitNoteNo(String(noteNo));
				}

				const rawDate = header.dn_debit_note_date;
				if (rawDate) {
					const dateStr = String(rawDate);
					if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
						setDebitNoteDate(dateStr.slice(0, 10));
					} else if (dateStr.includes('/')) {
						const parts = dateStr.split('/');
						if (parts.length === 3) {
							setDebitNoteDate(
								`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
							);
						}
					} else {
						const apiDate = new Date(rawDate);
						if (!Number.isNaN(apiDate.getTime())) {
							const y = apiDate.getFullYear();
							const m = String(apiDate.getMonth() + 1).padStart(2, '0');
							const d = String(apiDate.getDate()).padStart(2, '0');
							setDebitNoteDate(`${y}-${m}-${d}`);
						}
					}
				}

				const supplierId = header.dn_supplier_id;
				const supplier = supplierList.find(
					(e) => Number(e?.sm_supplier_code) === Number(supplierId)
				);
				if (supplier) {
					const typeJson = SUPPLIERTYPE.find((e) => e?.value === supplier?.supplier_type);
					if (typeJson) {
						setSupplierType(typeJson);
						const supplier_array = supplierList.filter((e) => e.supplier_type === typeJson.value);
						setSupplierNameOptions(supplier_array || []);
					}
					setSupplierName({
						label: supplier.sm_supplier_name,
						value: supplier.sm_supplier_code,
						supplier_type: supplier.supplier_type,
					});
				} else if (supplierId) {
					setSupplierName({
						label: String(supplierId),
						value: Number(supplierId),
					});
				}

				setNarration(header.dn_narration || header.narration || '');
				setGstPercent(Number(header.dn_gst_percent) || 5);
				setLoadedTotals({
					subtotal: Number(header.dn_sub_total ?? header.dn_subtotal ?? 0),
					gst_amount: Number(header.dn_gst_amount ?? 0),
					round_off: Number(
						header.dn_round_off ?? header.dn_rounded_off ?? header.round_off ?? 0
					),
					total_qty: Number(header.dn_total_qty ?? 0),
					total_amount: Number(header.dn_total_amount ?? 0),
				});
			}

			if (details.length > 0) {
				const mergedByItemRate = [];
				details.forEach((item) => {
					const name = sanitizeItemName(
						item.item_name || item.im_item_name || ''
					).trim();
					const qty = Number(item.quantity || 0);
					const rate = Number(item.rate || 0);
					const existing = mergedByItemRate.find(
						(row) =>
							String(row.item_name).toLowerCase() === name.toLowerCase() &&
							Number(row.rate) === rate
					);
					if (existing) {
						existing.qty = Number(existing.qty) + qty;
						existing.amount = Formatting(
							'two_decimal',
							Number(existing.qty) * rate
						);
						return;
					}
					mergedByItemRate.push({
						sno: mergedByItemRate.length + 1,
						item_name: name,
						qty,
						rate,
						amount: Formatting('two_decimal', qty * rate),
					});
				});
				setItemDetails(
					mergedByItemRate.map((row, index) => ({ ...row, sno: index + 1 }))
				);
			} else {
				setItemDetails([]);
			}
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			console.error('Error loading debit note:', error);
			if (error.code === COMMONVARIABLES.STATUS.NETWORK_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.NETWORK_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
			if (error.code === COMMONVARIABLES.STATUS.BAD_REQUEST_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.BAD_REQUEST_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		}
	}

	const clearItemValues = () => {
		setCurrentItemName('');
		setCurrentQty('');
		setCurrentRate('');
		setCurrentAmount(0);
		setIsEdit(0);
		setCurrentSno(0);
	};

	const udfnAddItem = (e) => {
		e?.preventDefault?.();
		let error = '';
		const itemName = sanitizeItemName(currentItemName).trim();

		if (!itemName) {
			error = ERROR.COMMON_REQUIRED;
		} else if (!ITEM_NAME_PATTERN.test(itemName)) {
			error = ERROR.DEBIT_NOTE.ITEM_NAME_VALID;
		} else if (currentQty === '' || Number(currentQty) <= 0) {
			error = ERROR.DEBIT_NOTE.ITEM_QTY_VALID;
		} else if (currentRate === '' || Number(currentRate) < 0) {
			error = ERROR.DEBIT_NOTE.ITEM_RATE_VALID;
		}

		if (error) {
			toast.error(error, { position: 'top-right', autoClose: 5000 });
			return;
		}

		const qtyToAdd = Number(currentQty);
		const rateValue = Number(currentRate);

		const isSameItemRate = (row) =>
			String(row.item_name || '').toLowerCase() === itemName.toLowerCase() &&
			Number(row.rate) === rateValue;

		const renumber = (list) =>
			list.map((row, index) => ({ ...row, sno: index + 1 }));

		// Edit: drop the row being edited first, then merge qty into same item+rate (or insert).
		// Add: merge qty into existing same item+rate, else append.
		setItemDetails((prev) => {
			const baseList =
				isEdit === 1 ? prev.filter((row) => row.sno !== currentSno) : prev;
			const existingIndex = baseList.findIndex(isSameItemRate);

			if (existingIndex > -1) {
				return renumber(
					baseList.map((row, index) => {
						if (index !== existingIndex) return row;
						const newQty = Number(row.qty) + qtyToAdd;
						return {
							...row,
							qty: newQty,
							amount: Formatting('two_decimal', newQty * rateValue),
						};
					})
				);
			}

			return renumber([
				...baseList,
				{
					sno: baseList.length + 1,
					item_name: itemName,
					qty: qtyToAdd,
					rate: rateValue,
					amount: Formatting('two_decimal', qtyToAdd * rateValue),
				},
			]);
		});
		clearItemValues();
	};

	const udfnLoadItem = (e, editItem, type) => {
		e?.preventDefault?.();
		setIsEdit(type);
		setCurrentSno(editItem?.sno);
		setCurrentItemName(editItem?.item_name || '');
		setCurrentQty(editItem?.qty);
		setCurrentRate(editItem?.rate);
		setCurrentAmount(editItem?.amount);
	};

	const udfnDeleteItem = (sno) => {
		swal({
			title: 'Are you sure?',
			text: 'Do you want to delete this item?',
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then((willDelete) => {
			if (!willDelete) return;
			const updated = itemDetails
				.filter((item) => item.sno !== sno)
				.map((item, index) => ({ ...item, sno: index + 1 }));
			setItemDetails(updated);
			if (currentSno === sno) {
				clearItemValues();
			}
		});
	};

	const udfnResetForm = () => {
		setSupplierName({});
		setSupplierAddress('');
		setSupplierCity('');
		setSupplierContact('');
		setSupplierGstin('');
		setNarration('');
		setItemDetails([]);
		setLoadedTotals(null);
		setGstPercent(5);
		clearItemValues();
		if (transNo === 0) {
			setDebitNoteDate(new Date().toISOString().slice(0, 10));
			fetchMasterData();
		}
	};

	const validateForm = () => {
		if (!supplierName?.value || !debitNoteDate) {
			return ERROR.COMMON_REQUIRED;
		}
		if (itemDetails.length < 1) {
			return ERROR.DEBIT_NOTE.ITEM_DETAILS_VALID;
		}
		return '';
	};

	const buildItemPayload = () =>
		itemDetails.map((row) => ({
			item_name: String(row.item_name || '').trim(),
			quantity: Number(row.qty),
			rate: Number(row.rate),
			amount: Number(row.amount),
		}));

	const buildDebitNotePayload = (isUpdate = false) => {
		const user = getLoggedInUser();
		const localFy = localStorage.getItem('fy');
		// Save / Update contract — totals calculated on backend; UI-only on frontend
		const params = {
			debit_note_date: debitNoteDate,
			supplier_id: Number(supplierName?.value),
			narration: narration || '',
			user_id: Number(user?.id),
			company_code: Number(user?.company_code),
			fy: Number(localFy),
			item_details: buildItemPayload(),
		};
		if (isUpdate) {
			params.debit_note_id = Number(transNo);
		}
		return params;
	};

	const onClickSaveBtn = async (event) => {
		if (transNo === 0) {
			await udfnSaveDebitNote(event);
		} else {
			await udfnUpdateDebitNote(event);
		}
	};

	const formatPrintDate = (rawDate) => {
		// Display as DD/MM/YYYY
		if (!rawDate) return '';
		const dateStr = String(rawDate).trim();
		if (dateStr.includes('/')) {
			const parts = dateStr.split('/');
			if (parts.length === 3) {
				const d = parts[0].padStart(2, '0');
				const m = parts[1].padStart(2, '0');
				const y = parts[2];
				return `${d}/${m}/${y}`;
			}
			return dateStr;
		}
		if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
			const [y, m, d] = dateStr.slice(0, 10).split('-');
			return `${d}/${m}/${y}`;
		}
		const apiDate = new Date(rawDate);
		if (!Number.isNaN(apiDate.getTime())) {
			const y = apiDate.getFullYear();
			const m = String(apiDate.getMonth() + 1).padStart(2, '0');
			const d = String(apiDate.getDate()).padStart(2, '0');
			return `${d}/${m}/${y}`;
		}
		return dateStr;
	};

	const mapPrintApiToPrintData = (body) => {
		const headerList = body?.debitNoteList || [];
		const header = Array.isArray(headerList) && headerList.length > 0 ? headerList[0] : null;
		const items = Array.isArray(body?.debitNoteItemList) ? body.debitNoteItemList : [];

		if (!header) {
			return null;
		}

		const itemDetailsMapped = items.map((item) => ({
			item_name: item.item_name || item.im_item_name || '',
			quantity: Number(item.quantity || 0),
			rate: Number(item.rate || 0),
			amount: Number(item.amount || 0),
		}));
		const computedSubtotal = itemDetailsMapped.reduce(
			(sum, row) => sum + (Number(row.amount) || 0),
			0
		);
		const gstPct = Number(header.dn_gst_percent) || 5;
		const computedSubtotalVal =
			header.dn_sub_total != null || header.dn_subtotal != null
				? Number(header.dn_sub_total ?? header.dn_subtotal)
				: computedSubtotal;
		const computedGst =
			header.dn_gst_amount != null
				? Number(header.dn_gst_amount)
				: Number(Formatting('two_decimal', computedSubtotalVal * (gstPct / 100)));
		const beforeRound = Formatting(
			'two_decimal',
			Number(computedSubtotalVal) + Number(computedGst)
		);
		const computedRoundOff =
			header.dn_round_off != null || header.dn_rounded_off != null
				? Number(header.dn_round_off ?? header.dn_rounded_off)
				: Number(
						Formatting(
							'two_decimal',
							Number(Formatting('round_off', beforeRound)) - Number(beforeRound)
						)
				  );
		const computedGrand =
			header.dn_total_amount != null
				? Number(header.dn_total_amount)
				: Number(Formatting('round_off', beforeRound));
		const computedQty =
			header.dn_total_qty != null
				? Number(header.dn_total_qty)
				: itemDetailsMapped.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);

		return {
			debit_note_id: header.dn_debit_note_id || 0,
			debit_note_no: header.dn_debit_note_number ?? '',
			debit_note_date: formatPrintDate(header.dn_debit_note_date),
			supplier_name: header.sm_supplier_name || '',
			supplier_address: header.sm_address || '',
			supplier_city: header.sm_city || '',
			supplier_gstin: header.sm_gstin || '',
			supplier_contact: header.sm_mobile_number || '',
			narration: header.dn_narration || header.narration || '',
			subtotal: computedSubtotalVal,
			gst_percent: gstPct,
			gst_amount: computedGst,
			round_off: computedRoundOff,
			total_amount: computedGrand,
			total_qty: computedQty,
			item_details: itemDetailsMapped,
		};
	};

	const fetchPrintData = async (debitNoteId) => {
		const user = getLoggedInUser();
		const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
		const printRes = await axios.post(
			BASE_URL + DEBIT_NOTE_API.PRINT_DEBIT_NOTE,
			{
				user_id: Number(user?.id),
				debit_note_id: Number(debitNoteId),
			},
			config
		);

		const status = printRes?.data?.body?.status ?? printRes?.data?.status;
		if (status === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
			await handleUnauthorized(() => fetchPrintData(debitNoteId));
			return null;
		}
		if (status === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS) {
			throw new Error(ERROR.API_ERROR.SERVICE_CATCH_ERROR);
		}

		const body = printRes?.data?.body;
		const mapped = mapPrintApiToPrintData(body);
		if (!mapped) {
			throw new Error('Print data not found');
		}
		return mapped;
	};

	const runPrintFlow = async (savedId) => {
		try {
			setIsLoading(true);
			const data = await fetchPrintData(savedId || 0);
			setIsLoading(false);
			if (!data) {
				navigate('/debit-note-list');
				return;
			}
			setPrintData(data);
			setIsPrint(1);
		} catch (e) {
			setIsLoading(false);
			console.error('Debit note print API error', e);
			toast.error(
				e?.message || ERROR.API_ERROR.SERVICE_CATCH_ERROR,
				{ position: toast.POSITION.TOP_RIGHT }
			);
			navigate('/debit-note-list');
		}
	};

	const askPrintConfirmation = (savedId, successText) => {
		// Popup 1: Success only
		swal({
			title: 'Success',
			text: successText,
			icon: 'success',
			button: 'OK',
			dangerMode: false,
		}).then(() => {
			// Popup 2: Print confirmation (separate)
			swal({
				title: 'Print Debit Note',
				text: 'Do you want to print the Debit Note invoice?',
				icon: 'warning',
				buttons: ['No', 'Yes'],
				dangerMode: false,
			}).then(async (willPrint) => {
				if (willPrint) {
					await runPrintFlow(savedId);
				} else {
					navigate('/debit-note-list');
				}
			});
		});
	};

	const udfnSaveDebitNote = async (event) => {
		try {
			event?.preventDefault?.();
			const error = validateForm();
			if (error) {
				toast.error(error, { position: 'top-right', autoClose: 5000 });
				return;
			}

			setButtonFlag(true);
			const params = buildDebitNotePayload(false);
			const user = getLoggedInUser();
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
			const saveData = await axios.post(BASE_URL + DEBIT_NOTE_API.SAVE_DEBIT_NOTE, params, config);
			setButtonFlag(false);

			const status = saveData?.data?.body?.status ?? saveData?.data?.status;
			if (status === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
				await handleUnauthorized(() => udfnSaveDebitNote());
				return;
			}
			if (status === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS) {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
				return;
			}
			if (status === COMMONVARIABLES.STATUS.SUCCESS) {
				const savedId =
					saveData?.data?.body?.debit_note_id ||
					saveData?.data?.body?.dn_debit_note_id ||
					0;
				// Save (Add): success then print — no print confirmation
				swal({
					title: 'Success',
					text: 'Debit note saved successfully',
					icon: 'success',
					button: 'OK',
					dangerMode: false,
				}).then(async () => {
					await runPrintFlow(savedId);
				});
			} else if (saveData?.data?.body?.message) {
				toast.error(saveData.data.body.message, { position: toast.POSITION.TOP_RIGHT });
			}
		} catch (e) {
			setButtonFlag(false);
			if (e.code === COMMONVARIABLES.STATUS.NETWORK_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.NETWORK_ERROR, { position: toast.POSITION.TOP_RIGHT });
			} else if (e.code === COMMONVARIABLES.STATUS.BAD_REQUEST_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.BAD_REQUEST_ERROR, { position: toast.POSITION.TOP_RIGHT });
			} else {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		}
	};

	const udfnUpdateDebitNote = async (event) => {
		try {
			event?.preventDefault?.();
			const error = validateForm();
			if (error) {
				toast.error(error, { position: 'top-right', autoClose: 5000 });
				return;
			}

			setButtonFlag(true);
			const params = buildDebitNotePayload(true);
			const user = getLoggedInUser();
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
			const updateData = await axios.post(BASE_URL + DEBIT_NOTE_API.UPDATE_DEBIT_NOTE, params, config);
			setButtonFlag(false);

			const status = updateData?.data?.body?.status ?? updateData?.data?.status;
			if (status === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
				await handleUnauthorized(() => udfnUpdateDebitNote());
				return;
			}
			if (status === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS) {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
				return;
			}
			if (status === COMMONVARIABLES.STATUS.SUCCESS) {
				const savedId =
					updateData?.data?.body?.debit_note_id ||
					updateData?.data?.body?.dn_debit_note_id ||
					transNo;
				// Update: success popup, then separate print confirmation
				askPrintConfirmation(savedId, 'Debit note updated successfully');
			} else if (updateData?.data?.body?.message) {
				toast.error(updateData.data.body.message, { position: toast.POSITION.TOP_RIGHT });
			}
		} catch (e) {
			setButtonFlag(false);
			if (e.code === COMMONVARIABLES.STATUS.NETWORK_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.NETWORK_ERROR, { position: toast.POSITION.TOP_RIGHT });
			} else if (e.code === COMMONVARIABLES.STATUS.BAD_REQUEST_ERROR_STATUS) {
				toast.error(ERROR.API_ERROR.BAD_REQUEST_ERROR, { position: toast.POSITION.TOP_RIGHT });
			} else {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		}
	};

	return (
		<>
			<ToastContainer />
			<LoadingOverlay active={isLoading} spinner text="Loading ...">
				<h4 className="card-title">{title}</h4>

				<div className="basic-form overlay">
					<form onSubmit={(e) => e.preventDefault()}>
						{/* Header:
						    Supplier                        Debit Note No *
						    Details                          Debit Date *
						*/}
						<div className="card">
							<div className="card-body">
								<Row className="align-items-start">
									{/* Left: Supplier + Details */}
									<Col lg={7}>
										<div className="row mb-2">
											<div className="form-group text-black font-w500 mb-2 col-lg-2 text-end">
												<label className="col-form-label text-black font-w500">
													Supplier <span className="text-danger">*</span>
												</label>
											</div>
											<div className="form-group text-black font-w300 mb-2 col-lg-3">
												<Select
													value={supplierType}
													onChange={(e) => {
														setSupplierName({});
														onChangeSupplierType(e);
													}}
													options={SUPPLIERTYPE}
													className="my-react-select-container"
													theme={selectTheme}
												/>
											</div>
											<div className="form-group text-black font-w300 mb-2 col-lg-7">
												<Select
													value={supplierName}
													onChange={setSupplierName}
													options={supplierNameOptions.map((option) => ({
														label: option.sm_supplier_name,
														value: option.sm_supplier_code,
														supplier_type: option.supplier_type,
													}))}
													placeholder="Select Supplier"
													className="my-react-select-container"
													theme={selectTheme}
												/>
											</div>
										</div>
										{supplierName?.value ? (
											<div className="row">
												<div className="col-lg-2" />
												<div className="col-lg-10">
													<div className="card-content border border-info pt-2 px-3">
														<address className="text-black mb-2">
															<strong>Supplier Details</strong>
															<br />
															{supplierAddress}
															<br />
															{supplierCity}
															{supplierContact ? (
																<>
																	<br />
																	Contact : {supplierContact}
																</>
															) : null}
															<br />
															<abbr title="GSTIN">GSTIN :{supplierGstin} </abbr>
														</address>
													</div>
												</div>
											</div>
										) : null}
									</Col>

									{/* Right: Debit Note No + Debit Date */}
									<Col lg={5}>
										<div className="row">
											<div className="form-group text-black font-w500 mb-2 mt-1 col-lg-5 text-end">
												<label className="col-form-label text-black font-w500">
													Debit Note No. <span className="text-danger">*</span>
												</label>
											</div>
											<div className="form-group text-black font-w300 mb-2 col-lg-7">
												<input
													type="text"
													className="form-control"
													placeholder="Debit Note No"
													value={debitNoteNo}
													readOnly
												/>
											</div>
											<div className="form-group text-black font-w500 mb-2 mt-1 col-lg-5 text-end">
												<label className="col-form-label text-black font-w500">
													Debit Date <span className="text-danger">*</span>
												</label>
											</div>
											<div className="form-group text-black font-w500 mb-2 col-lg-7">
												<input
													type="date"
													className="form-control"
													value={debitNoteDate}
													onChange={(e) => setDebitNoteDate(e.target.value)}
													max={new Date().toISOString().slice(0, 10)}
												/>
											</div>
										</div>
									</Col>
								</Row>
							</div>
						</div>

						{/* Item entry + table (Purchase style) */}
						<div className="card">
							<div className="card-body px-4 pt-3 py-0">
								<Row>
									<Col lg={12}>
										<div className="row purchase-form align-items-end mb-2">
											<div className="col-lg-4 mb-2">
												<label className="text-black font-w500">
													Item Name <span className="text-danger">*</span>
												</label>
												<input
													type="text"
													className="form-control"
													placeholder="Item Name"
													value={currentItemName}
													maxLength={50}
													onChange={(e) =>
														setCurrentItemName(sanitizeItemName(e.target.value))
													}
												/>
											</div>
											<div className="col-lg-2 mb-2">
												<label className="text-black font-w500">
													Qty <span className="text-danger">*</span>
												</label>
												<input
													type="number"
													className="form-control text-end"
													placeholder="Qty"
													value={currentQty}
													min="0"
													onKeyPress={(e) => {
														if (e.key === '-' || e.key === 'e') e.preventDefault();
													}}
													onChange={(e) => {
														if (e.target.value <= 9999) {
															setCurrentQty(e.target.value);
														}
													}}
												/>
											</div>
											<div className="col-lg-2 mb-2">
												<label className="text-black font-w500">
													Rate <span className="text-danger">*</span>
												</label>
												<input
													type="number"
													className="form-control text-end"
													placeholder="Rate"
													value={currentRate}
													min="0"
													onKeyPress={(e) => {
														if (e.key === '-' || e.key === 'e') e.preventDefault();
													}}
													onChange={(e) => {
														if (e.target.value <= 999999) {
															setCurrentRate(e.target.value);
														}
													}}
												/>
											</div>
											<div className="col-lg-2 mb-2">
												<label className="text-black font-w500">Amount</label>
												<input
													type="number"
													className="form-control text-end"
													placeholder="Amount"
													readOnly
													value={currentAmount}
												/>
											</div>
											<div className="col-lg-2 mb-2 d-flex align-items-end">
												{isEdit === 0 ? (
													<Button
														onClick={(e) => udfnAddItem(e)}
														className="me-1 btn-icon"
														variant="whatsapp"
													>
														<i className="fa fa-plus" />
													</Button>
												) : (
													<Button
														onClick={(e) => udfnAddItem(e)}
														className="me-1 btn-icon"
														variant="whatsapp"
													>
														<i className="fa fa-check" />
													</Button>
												)}
												<Button
													variant="danger"
													type="button"
													onClick={clearItemValues}
													className="btn btn-success btn-icon"
												>
													<i className="fa fa-ban" />
												</Button>
											</div>
										</div>
									</Col>

									<hr className="mt-1 mb-2 pt-0" />

									<Col xl={12}>
										<div className="row">
											<div className="table-responsive">
												<table className="table table-striped">
													<thead>
														<tr>
															<th width="5%">#</th>
															<th width="35%">Item Name</th>
															<th className="text-end" width="15%">Qty</th>
															<th className="text-end" width="15%">Rate</th>
															<th className="text-end" width="15%">Amount</th>
															<th className="text-center" width="15%">Actions</th>
														</tr>
													</thead>
													<tbody>
														{itemDetails.map((item) => (
															<tr
																key={item.sno}
																style={{
																	backgroundColor:
																		currentSno === item.sno ? '#2df1ff6e' : '',
																}}
															>
																<td>{item.sno}</td>
																<td className="text-black font-w500">{item.item_name}</td>
																<td align="right">{item.qty}</td>
																<td align="right">
																	{Number(item.rate).toLocaleString(undefined, {
																		minimumFractionDigits: 2,
																		maximumFractionDigits: 2,
																	})}
																</td>
																<td align="right">
																	{Number(item.amount).toLocaleString(undefined, {
																		minimumFractionDigits: 2,
																		maximumFractionDigits: 2,
																	})}
																</td>
																<td className="text-center">
																	<Link
																		className="btn btn-secondary shadow btn-xs sharp me-1"
																		onClick={(e) => udfnLoadItem(e, item, 1)}
																	>
																		<i className="fa fa-pencil" />
																	</Link>
																	<Link
																		className="btn btn-danger shadow btn-xs sharp"
																		onClick={() => udfnDeleteItem(item.sno)}
																	>
																		<i className="fa fa-trash" />
																	</Link>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</Col>
								</Row>

								<Row className="mb-3 mt-2">
									<Col lg={8} md={12} className="mb-3">
										<label className="text-black font-w300">Narration</label>
										<textarea
											className="form-control"
											rows={5}
											placeholder="Narration"
											value={narration}
											onChange={(e) => setNarration(e.target.value)}
											style={{ height: '120px', minHeight: '120px', resize: 'vertical' }}
										/>
									</Col>
									<Col lg={4} md={12}>
										<div className="mt-2 mb-3" style={{ marginLeft: 'auto', width: 'fit-content' }}>
											<table style={{ borderCollapse: 'collapse' }}>
												<tbody>
													<tr>
														<td className="text-black font-w500" style={{ whiteSpace: 'nowrap', width: '95px', padding: '4px 8px 4px 0' }}>
															Subtotal
														</td>
														<td className="text-black font-w500" style={{ padding: '4px 8px 4px 0' }}>:</td>
														<td className="text-black text-end" style={{ whiteSpace: 'nowrap', minWidth: '120px', padding: '4px 0' }}>
															<strong>
																{COMMONVARIABLES.India_Rupee_Formatter_Digits(subtotal)}
															</strong>
														</td>
													</tr>
													<tr>
														<td className="text-black font-w500" style={{ whiteSpace: 'nowrap', width: '95px', padding: '4px 8px 4px 0' }}>
															GST @{gstPercent}%
														</td>
														<td className="text-black font-w500" style={{ padding: '4px 8px 4px 0' }}>:</td>
														<td className="text-black text-end" style={{ whiteSpace: 'nowrap', minWidth: '120px', padding: '4px 0' }}>
															<strong>
																{COMMONVARIABLES.India_Rupee_Formatter_Digits(gstAmount)}
															</strong>
														</td>
													</tr>
													{Number(roundOff) !== 0 ? (
														<tr>
															<td className="text-black font-w500" style={{ whiteSpace: 'nowrap', width: '95px', padding: '4px 8px 4px 0' }}>
																Round Off
															</td>
															<td className="text-black font-w500" style={{ padding: '4px 8px 4px 0' }}>:</td>
															<td className="text-black text-end" style={{ whiteSpace: 'nowrap', minWidth: '120px', padding: '4px 0' }}>
																<strong>
																	{COMMONVARIABLES.India_Rupee_Formatter_Digits(roundOff)}
																</strong>
															</td>
														</tr>
													) : null}
													<tr>
														<td className="text-black font-w500" style={{ whiteSpace: 'nowrap', width: '95px', padding: '6px 8px 4px 0' }}>
															Grand Total
														</td>
														<td className="text-black font-w500" style={{ padding: '6px 8px 4px 0' }}>:</td>
														<td className="text-black text-end" style={{ whiteSpace: 'nowrap', minWidth: '120px', padding: '6px 0 4px'}}>
															<strong>
																{COMMONVARIABLES.India_Rupee_Formatter_Digits(grandTotal)}
															</strong>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
										<div className="text-end">
											<Link to="/debit-note-list">
												<Button
													variant="danger"
													type="button"
													className="btn btn-success me-2"
												>
													Cancel
												</Button>
											</Link>
											<Button
												variant="secondary"
												type="button"
												className="me-2"
												onClick={udfnResetForm}
											>
												Reset
											</Button>
											<Button
												className="me-2"
												variant="whatsapp"
												ref={buttonRef}
												onClick={onClickSaveBtn}
												disabled={buttonFlag}
											>
												{btnTitle}
											</Button>
										</div>
									</Col>
								</Row>
							</div>
						</div>
					</form>
				</div>

				{/* A5 Debit Note Invoice Print (frontend layout; content from API or sample) */}
				<div style={{ visibility: 'hidden', height: 0 }}>
					<div ref={contentToPrint} className="row purchsecontentToPrint" style={{ padding: '16px' }}>
						<table>
							<style>
								{`
									@page {
										size: A5 portrait;
										padding-left: 8px;
										padding-right: 0px;
									}
								`}
							</style>
							<thead>
								<tr>
									<td>
										<div className="printheader">
											<div className="col-12">
												<div className="row text-center">
													<div className="brand-logo p-16 col-4">
														<img
															className="logo-abbr me-3"
															src={Logo}
															alt=""
															style={{ width: '170px', height: '100px' }}
														/>
													</div>
													<div className="col-8 text-end">
														<span>
															<strong
																style={{ fontSize: 20, fontWeight: 700 }}
																className="d-block text-black"
															>
																{localUserDetails?.company_name}
															</strong>
														</span>
														<small className="text-black" style={{ fontSize: 16, lineHeight: 1.2 }}>
															{localUserDetails?.address}
															{', '}
															{localUserDetails?.city}
															<br />
															{localUserDetails?.state_name}
															<br />
															Email : {localUserDetails?.email_id}
															<br />
															Contact No : {localUserDetails?.mobile_no}
															<strong className="d-block">
																GSTIN : {localUserDetails?.gstin}
															</strong>
														</small>
													</div>
													<div className="h_line" />
													<strong
														style={{ fontSize: 16, fontWeight: 700 }}
														className="d-block text-center text-black"
													>
														DEBIT NOTE
													</strong>
												</div>
												<div className="h_line" />
												<div className="row">
													<div className="col-6">
														<span>
															<strong
																style={{ fontSize: 14, fontWeight: 500, marginBottom: '5px' }}
																className="d-block text-black"
															>
																Supplier :
															</strong>
														</span>
														<small className="text-black" style={{ fontSize: 12, lineHeight: 1.2 }}>
															{printData.supplier_name}
															<br />
															{printData.supplier_address}
															<br />
															{printData.supplier_city}
															{printData.supplier_contact ? (
																<>
																	<br />
																	Contact : {printData.supplier_contact}
																</>
															) : null}
															{printData.supplier_gstin ? (
																<>
																	<br />
																	GSTIN : {printData.supplier_gstin}
																</>
															) : null}
														</small>
													</div>
													<div className="col-6 text-end text-black">
														<table style={{ marginLeft: 'auto', borderCollapse: 'collapse' }}>
															<tbody>
																<tr>
																	<td
																		style={{
																			textAlign: 'right',
																			paddingBottom: '5px',
																			whiteSpace: 'nowrap',
																		}}
																	>
																		Debit Note No.&nbsp;:
																	</td>
																	<td
																		style={{
																			textAlign: 'left',
																			paddingBottom: '5px',
																			paddingLeft: '6px',
																			whiteSpace: 'nowrap',
																		}}
																	>
																		<strong>{printData.debit_note_no}</strong>
																	</td>
																</tr>
																<tr>
																	<td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
																		Date&nbsp;:
																	</td>
																	<td
																		style={{
																			textAlign: 'left',
																			paddingLeft: '6px',
																			whiteSpace: 'nowrap',
																		}}
																	>
																		<strong>{printData.debit_note_date}</strong>
																	</td>
																</tr>
															</tbody>
														</table>
													</div>
												</div>
												<div className="h_line" />
											</div>
										</div>
									</td>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>
										<div className="col-lg-12">
											<div className="table-responsive">
												<table className="table table-striped">
													<thead>
														<tr>
															<th className="center">#</th>
															<th className="center text-black">Item</th>
															<th className="center text-black">Qty</th>
															<th className="text-end text-black">Rate</th>
															<th className="text-end text-black">Amount</th>
														</tr>
													</thead>
													<tbody>
														{printData.item_details?.length > 0
															? printData.item_details.map((row, index) => (
																<tr key={index}>
																	<td className="text-black">{index + 1}</td>
																	<td className="text-black">{row.item_name}</td>
																	<td className="center text-black">{row.quantity}</td>
																	<td className="text-end text-black">
																		{COMMONVARIABLES.India_Rupee_Formatter_Digits(
																			row.rate
																		)}
																	</td>
																	<td className="text-end text-black">
																		{COMMONVARIABLES.India_Rupee_Formatter_Digits(
																			row.amount
																		)}
																	</td>
																</tr>
															))
															: null}
													</tbody>
												</table>
											</div>
											<div className="row mt-2">
												<div className="col-6 text-black">
													Remarks : {printData.narration}
												</div>
												<div className="col-6 text-black">
													<table
														style={{
															marginLeft: 'auto',
															borderCollapse: 'collapse',
															fontSize: '12px',
														}}
													>
														<tbody>
															<tr>
																<td
																	className="text-black"
																	style={{
																		whiteSpace: 'nowrap',
																		width: '95px',
																		padding: '2px 8px 2px 0',
																	}}
																>
																	Subtotal
																</td>
																<td
																	className="text-black"
																	style={{ padding: '2px 8px 2px 0', verticalAlign: 'top' }}
																>
																	:
																</td>
																<td
																	className="text-black"
																	style={{
																		textAlign: 'right',
																		whiteSpace: 'nowrap',
																		minWidth: '120px',
																		padding: '2px 0',
																	}}
																>
																	{COMMONVARIABLES.India_Rupee_Formatter_Digits(
																		printData.subtotal
																	)}
																</td>
															</tr>
															<tr>
																<td
																	className="text-black"
																	style={{
																		whiteSpace: 'nowrap',
																		width: '95px',
																		padding: '2px 8px 2px 0',
																	}}
																>
																	GST @{printData.gst_percent || gstPercent}%
																</td>
																<td
																	className="text-black"
																	style={{ padding: '2px 8px 2px 0', verticalAlign: 'top' }}
																>
																	:
																</td>
																<td
																	className="text-black"
																	style={{
																		textAlign: 'right',
																		whiteSpace: 'nowrap',
																		minWidth: '120px',
																		padding: '2px 0',
																	}}
																>
																	{COMMONVARIABLES.India_Rupee_Formatter_Digits(
																		printData.gst_amount
																	)}
																</td>
															</tr>
															{Number(printData.round_off) !== 0 ? (
																<tr>
																	<td
																		className="text-black"
																		style={{
																			whiteSpace: 'nowrap',
																			width: '95px',
																			padding: '2px 8px 2px 0',
																		}}
																	>
																		Round Off
																	</td>
																	<td
																		className="text-black"
																		style={{ padding: '2px 8px 2px 0', verticalAlign: 'top' }}
																	>
																		:
																	</td>
																	<td
																		className="text-black"
																		style={{
																			textAlign: 'right',
																			whiteSpace: 'nowrap',
																			minWidth: '120px',
																			padding: '2px 0',
																		}}
																	>
																		{COMMONVARIABLES.India_Rupee_Formatter_Digits(
																			printData.round_off
																		)}
																	</td>
																</tr>
															) : null}
															<tr>
																<td
																	className="text-black"
																	style={{
																		whiteSpace: 'nowrap',
																		width: '95px',
																		padding: '2px 8px 2px 0',
																	}}
																>
																	<strong>Grand Total</strong>
																</td>
																<td
																	className="text-black"
																	style={{ padding: '2px 8px 2px 0', verticalAlign: 'top' }}
																>
																	<strong>:</strong>
																</td>
																<td
																	className="text-black"
																	style={{
																		textAlign: 'right',
																		whiteSpace: 'nowrap',
																		minWidth: '120px',
																		padding: '2px 0',
																	}}
																>
																	<strong>
																		{COMMONVARIABLES.India_Rupee_Formatter_Digits(
																			printData.total_amount
																		)}
																	</strong>
																</td>
															</tr>
														</tbody>
													</table>
												</div>
											</div>
										</div>
									</td>
								</tr>
							</tbody>
							<tfoot>
								<tr>
									<td>
										<div className="footer-space" style={{ padding: 16 }}>
											<div className="printfooter">
												<div className="h_line" />
												<div style={{ marginTop: '35px' }}>
													<div className="row">
														<div className="col-4">
															<strong>Checked by</strong>
														</div>
														<div className="col-4">
															<strong>Verified by</strong>
														</div>
														<div className="col-4 text-end">
															<strong>Authorised Signatory</strong>
														</div>
													</div>
												</div>
											</div>
										</div>
									</td>
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			</LoadingOverlay>
		</>
	);
};

export default DebitNoteAdd;
